import type { KyInstance, Options as KyOptions } from 'ky';
import ky, { HTTPError } from 'ky';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { logError, suppressErrorLogging } from '@zg-rentals/log-error';
import { getTraceHeaders } from '@zg-rentals/trace';
import { makeAppInstance } from '@zg-rentals/util';

export type Client = KyInstance;
export type Options = KyOptions;
export { HTTPError };

// Whether monitoring is enabled for a given Client instance - keyed on the client itself since
// each `.extend()` call produces a distinct instance that should carry its own flag.
const monitoringFlags = new WeakMap<object, boolean>();

// requestOptions (beforeRequest/afterResponse) and error.options (beforeError) are the same
// object for a given request, so per-request state keyed on it is shared across every hook
// without mutating the object itself. Weakly held so an entry is collectable as soon as ky's
// per-request options object (a fresh one per request - see ky's Ky constructor) is unreachable.
const requestStartTimes = new WeakMap<object, number>();

function getLatencyMs(options: object): number | undefined {
  const startTime = requestStartTimes.get(options);
  return startTime === undefined ? undefined : Date.now() - startTime;
}

const PX_BLOCKED_HEADER = 'x-px-blocked';

// Defense-in-depth for responses that omit PX_BLOCKED_HEADER: sniff the body for PX's own
// client-side marker instead. Only attempted for non-2xx responses, since PX's block/captcha
// responses are never 2xx. Cloned so the original body stream stays intact for downstream
// consumers (e.g. httpApiRequest.ts's own response.text() read).
//
// afterResponse and beforeError each see a different Response object for the same request (ky
// clones the response before handing it to afterResponse), so the sniff result is cached on
// requestOptions/error.options instead - see requestStartTimes above.
const PX_CAPTCHA_MARKER = '_pxAppId';

// Which check caught the block, so callers can report it (e.g. to see which path is more common)
// without this function reaching for a monitor itself.
type PxBlockSource = 'header' | 'body';

const pxBodySniffResults = new WeakMap<object, Promise<PxBlockSource | false>>();

async function getPerimeterXBlockSource(
  response: Response | undefined,
  options: object | undefined,
): Promise<PxBlockSource | false> {
  if (!response) {
    return false;
  }

  if (response.headers.get(PX_BLOCKED_HEADER)) {
    return 'header';
  }

  if (response.ok || !response.headers.get('content-type')?.toLowerCase().includes('text/html')) {
    return false;
  }

  const cached = options && pxBodySniffResults.get(options);
  if (cached) {
    return cached;
  }

  const result = response
    .clone()
    .text()
    .then((text): PxBlockSource | false => (text.includes(PX_CAPTCHA_MARKER) ? 'body' : false))
    .catch(() => false as const);

  if (options) {
    pxBodySniffResults.set(options, result);
  }

  return result;
}

export const getClientOptions = (client: Client) =>
  ({
    monitoring: monitoringFlags.get(client) !== false,
  }) as const;

/**
 * Create a new HTTP client instance with full control over configuration.
 *
 * @note typically you want to use `getClient().extend({...overrides})` to simply
 * override the default configuration value(s) you need to override
 *
 * @note if you are looking to configure code generated clients, import `getClient` from
 * `@zg-rentals/ts-client-http-client`
 */
export const createClient = ({
  logging,
  monitoring,
  ...options
}: KyOptions & {
  logging?: boolean;
  monitoring?: boolean;
} = {}): Client => {
  const logger = () => (logging ? getGlobalLogger('http-client') : undefined);
  const monitor = () => (monitoring ? getGlobalMonitor() : undefined);

  const client = ky
    .create({
      hooks: {
        beforeRequest: [
          (request, requestOptions) => {
            // Instrument outbound fetch with W3C trace context (undici bypasses the http.request interception).
            if (!request.headers.has('traceparent')) {
              const traceHeaders = getTraceHeaders();
              if (traceHeaders?.traceparent) {
                request.headers.set('traceparent', traceHeaders.traceparent);
                if (traceHeaders.tracestate) {
                  request.headers.set('tracestate', traceHeaders.tracestate);
                }
              }
            }

            monitor()?.count({ name: 'httpClient beforeRequest volume total', amount: 1 });

            monitor()?.count({
              name: `httpClient beforeRequest volume method ${request.method} total`,
              amount: 1,
            });

            requestStartTimes.set(requestOptions, Date.now());
            // ky reuses this same options object across retry attempts of one request, so a
            // sniff result cached on it from a prior attempt would otherwise pin that attempt's
            // verdict onto every later attempt. beforeRequest fires fresh per attempt, so clear it here.
            pxBodySniffResults.delete(requestOptions);

            logger()?.debug(
              {
                url: request.url,
                method: request.method,
              },
              'beforeRequest',
            );
          },
        ],

        afterResponse: [
          async (request, requestOptions, response) => {
            monitor()?.count({ name: 'httpClient afterResponse volume total', amount: 1 });

            monitor()?.count({
              name: `httpClient afterResponse volume method ${request.method} total`,
              amount: 1,
            });

            monitor()?.count({
              name: `httpClient afterResponse volume statusCode ${response.status} total`,
              amount: 1,
            });

            const latencyMs = getLatencyMs(requestOptions);

            if (latencyMs !== undefined) {
              monitor()?.gauges({
                name: 'httpClient afterResponse latencyMs',
                amount: latencyMs,
              });

              monitor()?.gauges({
                name: `httpClient afterResponse latencyMs method ${request.method}`,
                amount: latencyMs,
              });
            }

            if (latencyMs !== undefined && latencyMs >= 30 * 1000) {
              logger()?.warn(
                {
                  url: request.url,
                  method: request.method,
                  status: response.status,
                  latencyMs,
                },
                'afterResponse slow request',
              );
            } else {
              logger()?.debug(
                {
                  url: request.url,
                  method: request.method,
                  status: response.status,
                  latencyMs,
                },
                'afterResponse',
              );
            }

            const pxBlockSource = await getPerimeterXBlockSource(response, requestOptions);
            if (pxBlockSource) {
              monitor()?.count(
                { name: 'httpClient px_blocked total', amount: 1 },
                { name: `httpClient px_blocked method ${request.method} total`, amount: 1 },
                { name: `httpClient px_blocked statusCode ${response.status} total`, amount: 1 },
                { name: `httpClient px_blocked source ${pxBlockSource} total`, amount: 1 },
              );
            }
          },
        ],

        beforeError: [
          async (error) => {
            const latencyMs = getLatencyMs(error.options);

            monitor()?.count({ name: 'httpClient beforeError volume total', amount: 1 });

            monitor()?.count({
              name: `httpClient beforeError volume method ${error.request?.method ?? 'UNKNOWN'} total`,
              amount: 1,
            });

            monitor()?.count({
              name: `httpClient beforeError volume statusCode ${error.response?.status ?? 'UNKNOWN'} total`,
              amount: 1,
            });

            if (latencyMs !== undefined) {
              monitor()?.gauges({
                name: 'httpClient beforeError latencyMs',
                amount: latencyMs,
              });

              monitor()?.gauges({
                name: `httpClient beforeError latencyMs method ${error.request?.method ?? 'UNKNOWN'} total`,
                amount: latencyMs,
              });
            }

            // Not re-counted here: ky always runs afterResponse for this same response before
            // beforeError (only for a non-ok response), so the px_blocked counter is already
            // incremented there - counting again here would double it.
            if (await getPerimeterXBlockSource(error.response, error.options)) {
              // Suppress regardless of `logging` so PX-blocked noise stays suppressed even for a
              // client that doesn't report its own generic errors - a later layer's `logError`
              // call (e.g. React Query) checks this same suppression state, so gating it behind
              // `logging` would let the PX noise leak through there instead.
              suppressErrorLogging(error);
            } else if (logging) {
              logError({
                error,
                context: {
                  url: error.request.url,
                  method: error.request.method,
                  status: error.response.status,
                  latencyMs,
                  traceparent: error.response.headers.get('traceparent'),
                  eid: error.response.headers.get('x-eid'),
                },
                errorType: 'HTTPError',
              });
            }

            return error;
          },
        ],
      },
    })
    .extend(options);

  // Note: this is an annoying hack, but allows us to scope monitoring/logging to the client, allowing
  // us to conditionally emit metrics in the codegen packages. This is necessary because we use the codegen
  // packages to emit metrics, so there is a recursive loop we need to avoid.
  monitoringFlags.set(client, monitoring === true);

  const extend = client.extend;
  client.extend = function (...args) {
    const extended = extend.call(this, ...args);
    monitoringFlags.set(extended, monitoring === true);
    return extended;
  };

  return client;
};

export const { get: getClient, set: setClient } = makeAppInstance(
  () => createClient({ logging: true, monitoring: true }),
  '@zg-rentals/http-client:client',
);
